import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Reveal } from "../lib/reveal";
import { Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFESSIONS = [
  "Architect",
  "Town Planner",
  "Landscape Architect",
  "Urban Designer",
  "Surveyor",
  "Ecologist",
  "GIS Specialist",
  "Other",
];

const GOALS = ["Grow my consultancy", "Improve process efficiency", "Both", "Not sure yet"];

const fmtDate = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const Booking = () => {
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [booked, setBooked] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", profession: "", goal: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadAvailability = useCallback(async (d) => {
    const iso = fmtDate(d);
    if (!iso) return;
    try {
      const { data } = await axios.get(`${API}/availability`, { params: { date: iso } });
      setSlots(data.slots || []);
      setBooked(data.booked || []);
    } catch (e) {
      setSlots(["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"]);
      setBooked([]);
    }
  }, []);

  useEffect(() => {
    if (date) {
      setSlot(null);
      loadAvailability(date);
    }
  }, [date, loadAvailability]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Please choose a date and time.");
      return;
    }
    if (!form.name || !form.email || !form.profession || !form.goal) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        date: fmtDate(date),
        time_slot: slot,
      });
      setDone(true);
      toast.success("Booking received — check your inbox for confirmation.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      if (err?.response?.status === 409) loadAvailability(date);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b-2 border-border rounded-none px-0 py-3 text-base focus:outline-none focus:border-primary transition-colors duration-200 placeholder:text-muted-foreground/60";

  return (
    <section id="booking" data-testid="booking-section" className="px-5 md:px-10 max-w-[1400px] mx-auto py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">05 / Book a call</p>
            <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tight leading-none">
              30 minutes. No slides.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              Pick a time that suits you. We'll talk through where your practice is
              now and where you want it to go — and whether we're the right fit to
              help you get there.
            </p>
            <ul className="mt-8 space-y-3">
              {["A candid read on your growth or delivery blockers", "One or two things you can action immediately", "No obligation, no hard sell"].map((t) => (
                <li key={t} className="flex gap-3 text-sm">
                  <Check size={18} className="text-primary shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <div data-testid="booking-success" className="bg-card border border-border p-10 md:p-14 text-center">
              <div className="w-16 h-16 mx-auto bg-primary text-primary-foreground flex items-center justify-center">
                <Check size={32} />
              </div>
              <h3 className="font-display font-bold text-3xl tracking-tight mt-6">You're booked in.</h3>
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                We've emailed a confirmation to <strong>{form.email}</strong>. We'll be in touch shortly to
                lock in {fmtDate(date)} at {slot}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="bg-card border border-border p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">Select a date</p>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 || d.getDay() === 6}
                    data-testid="booking-calendar"
                    className="border border-border p-3"
                  />
                </div>

                <div>
                  <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">Select a time</p>
                  {!date ? (
                    <p className="text-sm text-muted-foreground py-3">Choose a date to see available times.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                      {slots.map((s) => {
                        const taken = booked.includes(s);
                        const active = slot === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={taken}
                            data-testid={`slot-${s}`}
                            onClick={() => setSlot(s)}
                            className={`py-2.5 text-sm font-semibold border transition-colors duration-200 ${
                              taken
                                ? "border-border text-muted-foreground/40 line-through cursor-not-allowed"
                                : active
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border hover:border-primary"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Name *</label>
                  <input data-testid="booking-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" required />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Email *</label>
                  <input data-testid="booking-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@studio.com" required />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Company</label>
                  <input data-testid="booking-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Practice / firm" />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Discipline *</label>
                  <Select value={form.profession} onValueChange={update("profession")}>
                    <SelectTrigger data-testid="booking-profession" className="w-full bg-transparent border-0 border-b-2 border-border rounded-none px-0 mt-1 focus:ring-0">
                      <SelectValue placeholder="Select discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFESSIONS.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`profession-${p}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">What's the goal? *</label>
                  <Select value={form.goal} onValueChange={update("goal")}>
                    <SelectTrigger data-testid="booking-goal" className="w-full bg-transparent border-0 border-b-2 border-border rounded-none px-0 mt-1 focus:ring-0">
                      <SelectValue placeholder="Select a focus" />
                    </SelectTrigger>
                    <SelectContent>
                      {GOALS.map((g) => (
                        <SelectItem key={g} value={g} data-testid={`goal-${g}`}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Anything else?</label>
                  <textarea data-testid="booking-message" value={form.message} onChange={update("message")} rows={3} className={inputCls + " resize-none"} placeholder="A sentence on what you'd like to cover" />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="mt-10 w-full md:w-auto bg-foreground text-background font-semibold px-8 py-4 flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform duration-200 hover:hard-shadow disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Booking…</> : "Confirm booking"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
